import {
  partialMatchKey,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { PERSISTER_KEY_PREFIX } from '@tanstack/react-query-persist-client';
import { db } from '@/integrations/idb/db';

const queryKeyFromStorageKey = (storageKey: string): QueryKey | undefined => {
  const prefix = `${PERSISTER_KEY_PREFIX}-`;

  if (!storageKey.startsWith(prefix)) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(storageKey.slice(prefix.length));

    return Array.isArray(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const deletePersistedCopies = async (queryKey: QueryKey) => {
  const storageKeys = await db.tanstackQueries.toCollection().primaryKeys();
  const matched = storageKeys.filter((storageKey) => {
    const storedQueryKey = queryKeyFromStorageKey(storageKey);

    return (
      storedQueryKey !== undefined && partialMatchKey(storedQueryKey, queryKey)
    );
  });

  if (matched.length === 0) {
    return;
  }

  await db.tanstackQueries.bulkDelete(matched);
};

const clearPersistedCopies = async (queryKey: QueryKey) => {
  try {
    await deletePersistedCopies(queryKey);
  } catch (error) {
    console.error(error);
  }
};

export const invalidateQueries = async (
  queryClient: QueryClient,
  queryKey: QueryKey,
) => {
  await clearPersistedCopies(queryKey);
  await queryClient.invalidateQueries({ queryKey });
};

export const removeQueries = async (
  queryClient: QueryClient,
  queryKey: QueryKey,
) => {
  await clearPersistedCopies(queryKey);
  await queryClient.removeQueries({ queryKey });
};
